// sekcja blog na stronie głównej (Wise UI) - `/home/blog/articles/ARTICLE`, karuzela WiseCarousel + karty extended

import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { Slide, Slider } from 'pure-react-carousel';
import { Trans } from 'react-i18next';

import { useGetHomeBlogArticles } from 'api';
import { Link } from 'components/controls';
import { useRWD } from 'hooks';
import WiseBlogCard from 'components/wise/WiseBlogCard/WiseBlogCard';
import WiseCarousel from 'pages/wise/WiseProduct/components/WiseCarousel';

import styles from 'theme/components/wise/WiseBlog/WiseBlog.module.scss';

const BLOG_SECTION_SYMBOL = 'ARTICLE';

/** Szerokość karty bloga w karuzeli (wyjątek względem 260px produktów). */
const CARD_W = 365;
const CARD_H = 465;
const CAROUSEL_MAX_VISIBLE_SLIDES = 4;
const SLIDE_GAP_PX = 8;

function countCardsThatFitRow(widthPx: number): number {
  if (widthPx <= 0) {
    return 1;
  }
  const slot = CARD_W + SLIDE_GAP_PX;
  return Math.max(1, Math.floor((widthPx + SLIDE_GAP_PX) / slot));
}

function pathnameFromUrl(raw: string | undefined): string {
  if (!raw) {
    return '/knowledge-base';
  }
  try {
    const parsed = new URL(raw);
    return `${parsed.pathname || '/knowledge-base'}${parsed.search}`;
  } catch {
    return '/knowledge-base';
  }
}

const WiseBlog: FC = () => {
  const { isMobile } = useRWD();
  const contentRef = useRef<HTMLDivElement>(null);
  const [fitsInRow, setFitsInRow] = useState<number | null>(null);

  const { data, isLoading, isFetching } = useGetHomeBlogArticles(BLOG_SECTION_SYMBOL);

  const items = data?.items ?? [];
  const itemCount = items.length;

  const viewAllPath = useMemo(() => pathnameFromUrl(items[0]?.url_to_all_articles), [items]);

  const headerAction = (
    <Link className={styles.viewAllCta} to={viewAllPath}>
      <Trans>Zobacz wszystkie</Trans>
    </Link>
  );

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const measure = () => {
      setFitsInRow(countCardsThatFitRow(el.getBoundingClientRect().width));
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [itemCount]);

  const visibleSlidesInCarousel = useMemo(() => {
    if (itemCount < 1) {
      return 1;
    }
    if (isMobile) {
      return 1;
    }
    return Math.min(
      CAROUSEL_MAX_VISIBLE_SLIDES,
      itemCount,
      Math.max(1, fitsInRow ?? CAROUSEL_MAX_VISIBLE_SLIDES)
    );
  }, [itemCount, isMobile, fitsInRow]);

  const showNavArrows = !isMobile;

  if (isLoading || itemCount < 1) {
    return null;
  }

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Components-wise-WiseBlog'
      )}>
      <div ref={contentRef} className={styles.content}>
        <WiseCarousel
          className={styles.carousel}
          totalSlides={itemCount}
          visibleSlides={visibleSlidesInCarousel}
          naturalSlideWidth={CARD_W}
          naturalSlideHeight={CARD_H}
          relaxHeight
          title={<Trans i18nKey="wiseBlog.sectionTitle" />}
          headerAction={headerAction}
          showNavArrows={showNavArrows}
          disableKeyboard
          disableAnimation={isFetching}>
          <Slider>
            {items.map((article, index) => (
              <Slide key={article.id} index={index} className={styles.slide}>
                <div className={styles.slideInner}>
                  <WiseBlogCard variant="extended" article={article} />
                </div>
              </Slide>
            ))}
          </Slider>
        </WiseCarousel>
      </div>
    </div>
  );
};

export default WiseBlog;
